import { useCallback, useEffect, useMemo, useState } from 'react';
import { fetchDockingRecords } from '../api/docking';
import type { MarketProject } from '../types/market';
import {
  DEFAULT_DOCKING_FILTER,
  type DockingFilter,
  type DockingRecord,
  type DockingStatistics,
  type ProjectDockingSummary,
} from '../types/docking';
import {
  buildProjectDockingSummaries,
  countDockedProjectsByCategory,
  filterDockingRecords,
} from '../utils/dockingHelpers';

export interface UseDockingResult {
  records: DockingRecord[];
  /** 未筛选的全量对接记录（详情 / 跨模式计数用） */
  allRecords: DockingRecord[];
  loading: boolean;
  error: string | null;
  filter: DockingFilter;
  setFilter: React.Dispatch<React.SetStateAction<DockingFilter>>;
  statistics: DockingStatistics;
  projectSummaries: ProjectDockingSummary[];
  selectedProjectId: string | null;
  setSelectedProjectId: (id: string | null) => void;
  selectedProjectRecords: DockingRecord[];
  mapProjects: MarketProject[];
}

export function useDocking(allProjects: MarketProject[]): UseDockingResult {
  const [allRecords, setAllRecords] = useState<DockingRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [filter, setFilter] = useState<DockingFilter>(DEFAULT_DOCKING_FILTER);
  const [selectedProjectId, setSelectedProjectId] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await fetchDockingRecords();
      setAllRecords(data);
    } catch (e) {
      setError(e instanceof Error ? e.message : '对接记录加载失败');
      setAllRecords([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const projectsById = useMemo(() => {
    const map = new Map<string, MarketProject>();
    allProjects.forEach((p) => map.set(p.id, p));
    return map;
  }, [allProjects]);

  const records = useMemo(
    () => filterDockingRecords(allRecords, projectsById, filter),
    [allRecords, projectsById, filter],
  );

  const projectSummaries = useMemo(
    () => buildProjectDockingSummaries(records, projectsById),
    [records, projectsById],
  );

  const statistics = useMemo(
    () => countDockedProjectsByCategory(projectSummaries),
    [projectSummaries],
  );

  const mapProjects = useMemo(
    () => projectSummaries.map((s) => s.project),
    [projectSummaries],
  );

  useEffect(() => {
    if (!selectedProjectId) return;
    const exists = allProjects.some((p) => p.id === selectedProjectId);
    if (!exists) setSelectedProjectId(null);
  }, [allProjects, selectedProjectId]);

  const selectedProjectRecords = useMemo(() => {
    if (!selectedProjectId) return [];
    return allRecords
      .filter((r) => r.projectId === selectedProjectId)
      .sort((a, b) => b.dockedAt.localeCompare(a.dockedAt));
  }, [allRecords, selectedProjectId]);

  return {
    records,
    allRecords,
    loading,
    error,
    filter,
    setFilter,
    statistics,
    projectSummaries,
    selectedProjectId,
    setSelectedProjectId,
    selectedProjectRecords,
    mapProjects,
  };
}
